import { Engine } from 'engine/Engine';
import { Entity } from 'engine/Entity';
import { Facet } from 'engine/facets/Facet';
import { UIFacet } from 'engine/facets/UIFacet';
import { AudioManager } from 'engine/lib/AudioManager';
import { V2 } from 'engine/lib/V2';
import { create } from 'engine/lib/create';
import { MAX_BOT_HP } from 'game/config';
import { TitleScreen } from 'game/intro/TitleScreen';
import { BotToolChoice, ModuleType, NearbySelection, emptyBotState } from 'game/state/BotState';
import { RootState } from 'game/state/RootState';
import cloneDeep from 'lodash/cloneDeep';
import { createElement } from 'react';
import { MainWorld } from './MainWorld/MainWorld';

const ambiencePrairiePath = require('game/sfx/ambience-prairie.mp3');

const ambiencePrairie = new AudioManager(ambiencePrairiePath, { loop: true, volume: 0.8 });

const treePositions: V2[] = [
  [5470, 1079],
  [5734, 3403],
  [6817, 3703],
  [6197, 4089],
  [10224, 3893],
  [9484, 3699],
  [6407, 5859],
  [4424, 5549],
  [2434, 763],
  [1454, 853],
  [7254, 5669],
  [8514, 5913],
  [2584, 1929],
  [1517, 2319],
  [447, 5513],
  [3317, 5913],
  [334, 3663],
  [3627, 2703],
  [1057, 1769],
  [664, 2593],
  [3337, 1669],
  [494, 1469],
  [2334, 2653],
  [7670, 4440],
];
const treesChoppedPositions: V2[] = [
  [1240, 920],
  [2910, 990],
  [2910, 2290],
];

const rootState: RootState = {
  player: {
    position: [100, -4460],
    facing: 'right',
    speed: 0.55,
    hp: 100,
    didDie: false,
  },
  trees: [
    ...treePositions.map(position => ({ chopped: false, position })),
    ...treesChoppedPositions.map(position => ({ chopped: true, position })),
  ],
  bots: [
    {
      id: 'bot-id',
      position: [2442, 3760],
      botSpeed: 0.2,
      running: true,
      looping: true,
      cache: {},
      palette: {
        eyes: '#FFF383',
        body: '#0071bc',
        stroke: '#2E3192',
      },
      hp: 0,
      didDie: true,
      electrifiedEnd: null,
      inventory: [{
        type: BotToolChoice.WateringCan,
        count: 2,
      }],
      modules: [
        {
          id: 1,
          type: ModuleType.Cpu,
          position: [-1, 0],
          inputs: {
            cpu: { moduleId: 3, socketName: 'cmd' },
          },
          config: null,
        },
        {
          type: ModuleType.Nearby,
          id: 2,
          position: [-4, 0],
          inputs: {},
          config: {
            manualSelection: NearbySelection.DryPlant,
          },
        },
        {
          type: ModuleType.Use,
          id: 3,
          position: [-3, 0],
          inputs: {
            target: { moduleId: 2, socketName: 'found' },
          },
          config: {
            tool: BotToolChoice.WateringCan,
          },
        },
        {
          type: ModuleType.Nearby,
          id: 4,
          position: [-4, 1],
          inputs: {},
          config: {
            manualSelection: null,
          },
        },
        {
          type: ModuleType.Use,
          id: 5,
          position: [-3, 1],
          inputs: {
            target: { moduleId: 4, socketName: 'found' },
          },
          config: {
            tool: BotToolChoice.WateringCan,
          },
        },
      ],
    },
    {
      id: 'bot-id-2',
      position: [8100, 4800],
      botSpeed: 0.2,
      running: true,
      looping: true,
      cache: {},
      palette: {
        eyes: '#3FA9F5',
        body: '#FF1D25',
        stroke: '#2E3192',
      },
      hp: MAX_BOT_HP,
      didDie: false,
      electrifiedEnd: null,
      inventory: [{
        type: BotToolChoice.Laser,
        count: 1,
      }],
      modules: [
        {
          id: 1,
          type: ModuleType.Cpu,
          position: [-1, 0],
          inputs: {
            cpu: { moduleId: 5, socketName: 'out' },
          },
          config: null,
        },
        {
          type: ModuleType.Nearby,
          id: 2,
          position: [-4, 0],
          inputs: {},
          config: {
            manualSelection: NearbySelection.Human,
          },
        },
        {
          type: ModuleType.Use,
          id: 3,
          position: [-3, 0],
          inputs: {
            target: {
              moduleId: 2,
              socketName: 'found',
            },
          },
          config: {
            tool: BotToolChoice.Laser,
          },
        },
        {
          type: ModuleType.MoveTo,
          id: 4,
          position: [-4, 1],
          inputs: {},
          config: {
            target: [8100, 4800],
          },
        },
        {
          type: ModuleType.Logic,
          id: 5,
          position: [-2, 0],
          inputs: {
            3: {
              moduleId: 3,
              socketName: 'cmd',
            },
            4: {
              moduleId: 4,
              socketName: 'cmd',
            },
          },
          config: null,
          operation: 'fallback',
        },
      ],
    },
  ],
  labDoor: {
    ...cloneDeep(emptyBotState),
    running: true,
    looping: true,
    botSpeed: 0.025,
    modules: [
      {
        id: 1,
        type: ModuleType.Cpu,
        position: [-1, 0],
        inputs: {
          cpu: { moduleId: 2, socketName: 'open' },
        },
        config: null,
      },
      {
        id: 2,
        type: ModuleType.Door,
        position: [-3, 0],
        inputs: {
          pwr: { moduleId: 3, socketName: 'accepted' },
        },
        config: null,
      },
      {
        id: 3,
        type: ModuleType.Scanner,
        position: [-5.5, 0],
        inputs: {},
        config: null,
      },
    ],
  },
};

class GameManagerBehavior extends Facet {
  load(newRootState: RootState, engine: Engine) {
    engine.entities.forEach(e => {
      if (e !== this.entity) engine.destroy(e);
    });
    engine.add(create(MainWorld, {
      rootState: newRootState,
      reload: () => {
        engine.time.timeScale = 1;
        this.load(cloneDeep(rootState), engine);
      },
    }));
  }

  start(engine: Engine) {
    this.load(cloneDeep(rootState), engine);
  }
}

interface GameManagerProps {
  onStart: () => void,
}

export class GameManager extends Entity<GameManagerProps> {
  createFacets(props: GameManagerProps) {
    const titleScreen = create(UIFacet, { Component: () => null });
    const onStart = (engine: Engine) => {
      ambiencePrairie.play();
      this.addFacet(create(GameManagerBehavior, {}), engine);
      this.removeFacet(titleScreen);
      props.onStart();
    };
    titleScreen.props.Component = () => createElement(TitleScreen, { onStart });

    return [titleScreen];
  }
}
